import { createContext, useContext, useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import axios, { AxiosInstance } from "axios";
import {
  onIdTokenChanged,
  signInWithCustomToken,
  type User,
} from "firebase/auth";
import { useCookies } from "react-cookie";

import { LoadingPage } from "@/components/pages";
import {
  AI_SERVICE_HOSTNAME,
  API_HOSTNAME,
  GUEST_TOKEN_COOKIE_NAME,
  SESSION_TOKEN_COOKIE_NAME,
  TUTRED_CLIENT_HOSTNAME,
} from "@/constants";
import { UserRole } from "@/types";
import { auth } from "@/utils/firebase";

const firebaseContext = createContext({
  user: null as User | null,
  loading: true,
  userRole: UserRole.Student,
  sessionToken: null as string | null,
  authenticatedAxios: {} as AxiosInstance,
  aiAxios: {} as AxiosInstance,
});

export const FirebaseContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const currentUrl = window.location.href;
  const tokenRefreshUrl = `${TUTRED_CLIENT_HOSTNAME}login?redirect=${currentUrl}`;
  const axiosInstance = useRef(
    axios.create({
      baseURL: API_HOSTNAME,
      timeout: 30000,
    })
  );
  const [, setError] = useState<string | null>(null);
  const [cookies, setCookie, removeCookie] = useCookies([SESSION_TOKEN_COOKIE_NAME, GUEST_TOKEN_COOKIE_NAME]);
  const sessionCookie = cookies[SESSION_TOKEN_COOKIE_NAME] as string | undefined;
  const guestCookie = cookies[GUEST_TOKEN_COOKIE_NAME] as string | undefined;
  const [loading, setLoading] = useState(true);
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(UserRole.Student);
  const aiAxiosInstance = useRef(
    axios.create({
      baseURL: AI_SERVICE_HOSTNAME,
      timeout: 30000,
    })
  );

  useEffect(() => {
    const handleAuth = async () => {
      // await auth.authStateReady();
      // if (auth.currentUser) {
      //   return;
      // }

      // Check for guestToken in URL parameters or cookies
      const urlParams = new URLSearchParams(window.location.search);
      const urlGuestToken = urlParams.get("guestToken");
      const cookieGuestToken = guestCookie;
      const guestToken = urlGuestToken || cookieGuestToken;
      const token = urlParams.get("token");
      const isProduction = import.meta.env.VITE_PUBLIC_STAGE === "production";
      const isSandbox =
        (window.location.pathname.startsWith("/sandbox") ||
          urlParams.get("sandbox") === "true") &&
        !isProduction;
      if (isSandbox) {
        setIsFetchingToken(true);
        try {
          const requestedRole = urlParams.get("role") || "tutor";
          const res = await axios.post(`${API_HOSTNAME}auth/sandbox-token`, {
            role: requestedRole
          });
          if (res.data.success) {
            await signInWithCustomToken(auth, res.data.token);
          }
        } catch (error) {
          console.error("Error authenticating to sandbox:", error);
          setError("Error authenticating to sandbox");
        }
        setIsFetchingToken(false);
        return;
      }


      if (token) {
        try {
          // Hit the updated endpoint, passing token as query param
          const response = await axios.get(
            `${API_HOSTNAME}auth/login-with-token`,
            {
              params: { token }, // token in query, per backend update
            }
          );
          await signInWithCustomToken(auth, response.data.token);
          setIsFetchingToken(false);
          return;
        } catch (error) {
          console.error("Error during login with token:", error);
          setError("Error during login with token");
        }
      } else if (guestToken) {
        // Skip cookie login if guestToken is present
        const pathParts = window.location.pathname.split('/').filter(part => part !== '');
        const lessonId = pathParts[0]; // First non-empty part is the lessonId

        try {
          setIsFetchingToken(true);
          const res = await axios.post(`${API_HOSTNAME}auth/validate-guest-token`, {
            guestToken,
            lessonId,
          });

          if (res.status === 200 && res.data.success) {
            // If token came from URL, store it as a cookie
            if (urlGuestToken && !cookieGuestToken) {
              const expiresAt = new Date(res.data.expiryTime);
              setCookie(GUEST_TOKEN_COOKIE_NAME, guestToken, {
                expires: expiresAt,
                path: '/'
              });
            }
            if (res.data.signInWishCustomToken) {
              await signInWithCustomToken(auth, res.data.token);
            }
            setIsFetchingToken(false);
            return; // Exit early, don't proceed to cookie auth
          } else {
            console.error("Error validating guest token:", res.data.error);
            // Clear invalid guest token cookie
            if (cookieGuestToken) {
              removeCookie(GUEST_TOKEN_COOKIE_NAME);
            }
          }
        } catch (error) {
          console.error("Error validating guest token:", error);
          // Clear invalid guest token cookie
          if (cookieGuestToken) {
            removeCookie(GUEST_TOKEN_COOKIE_NAME);
          }
        }
      }
      // Only proceed with cookie auth if no guest token or guest token validation failed
      if (sessionCookie) {
        setIsFetchingToken(true);
        axios
          .get(`${API_HOSTNAME}auth/login-with-cookie`, {
            headers: {
              Authorization: `Bearer ${sessionCookie}`,
            },
          })
          .then(async (res) => {
            await signInWithCustomToken(auth, res.data.token);
            setIsFetchingToken(false);
          })
          .catch((error) => {
            console.error("Error during login with cookie:", error);
          });
      } else {
        // No session cookie, go to app to intialize it
        window.location.href = tokenRefreshUrl;
      }
    };
    handleAuth();
    // Depend on cookie *values*, not the cookies object (react-cookie gives a new object every render).
  }, [sessionCookie, guestCookie, setCookie, removeCookie, tokenRefreshUrl]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, (user) => {
      if (isFetchingToken) return;
      if (user) {
        user?.getIdTokenResult().then((result) => {
          const userRole = result?.claims.role;
          setUserRole(userRole as UserRole);
          axiosInstance.current.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${result.token}`;
          aiAxiosInstance.current.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${result.token}`;
          setUser(user);
          setLoading(false);
          Sentry.setUser({
            id: user.uid,
            email: user.email || '',
            name: user.displayName,
          });
        });
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => unsub();
  }, [isFetchingToken]);

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <firebaseContext.Provider
      value={{
        sessionToken: sessionCookie ?? null,
        user,
        loading,
        userRole,
        authenticatedAxios: axiosInstance.current,
        aiAxios: aiAxiosInstance.current,
      }}
    >
      {children}
    </firebaseContext.Provider>
  );
};

/**
 * Hook to access the Firebase context.
 * This hook provides access to the current user, loading states, user role, session token, and an authenticated Axios instance.
 */
const useFirebaseContext = (): {
  user: User | null;
  loading: boolean;
  userRole: UserRole;
  sessionToken: string | null;
  authenticatedAxios: AxiosInstance;
  aiAxios: AxiosInstance;
} => useContext(firebaseContext);

export default useFirebaseContext;
